import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import NewsletterSignup from '../../../components/ecommerce/NewsletterSignup';
import { usePublications } from '../../../hooks/usePublications';
import { useTenant } from '../../../contexts/TenantContext';
import { useCustomer } from '../../../contexts/CustomerContext';
import { marquerFilCommeVu } from '../../../utils/publicationsSeen';
import PublicationCard from './PublicationCard';
import PublicationModal from './PublicationModal';
import './Feed.css';

interface FeedProps {
  tenantId?: string;
  customerId?: string;
}

const Feed: React.FC<FeedProps> = ({ tenantId: propTenantId, customerId: propCustomerId }) => {
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const { customer } = useCustomer();

  const tenantId = propTenantId || tenant?.id || '';
  const customerId = propCustomerId || customer?.id;

  // Ouvrir le fil éteint le compteur posé sur « Actualités ».
  useEffect(() => {
    marquerFilCommeVu();
  }, []);

  const [selectedPublicationId, setSelectedPublicationId] = useState<string | null>(null);

  // Retour d'un lien reçu par email (confirmation / désinscription) : l'API
  // redirige ici avec ?newsletter=… ; on affiche le résultat puis on nettoie l'URL.
  const [searchParams, setSearchParams] = useSearchParams();
  const newsletterState = searchParams.get('newsletter');
  const newsletterMessage: Record<string, { text: string; tone: 'ok' | 'warn' }> = {
    confirmed: { text: '✅ Abonnement confirmé : vous recevrez nos actualités par email. Merci !', tone: 'ok' },
    unsubscribed: { text: 'Vous êtes désabonné(e). Vous ne recevrez plus nos actualités par email.', tone: 'ok' },
    invalid: { text: 'Ce lien n\'est plus valable. Inscrivez-vous à nouveau ci-dessous si besoin.', tone: 'warn' },
  };
  const dismissNewsletterMessage = () => {
    searchParams.delete('newsletter');
    setSearchParams(searchParams, { replace: true });
  };

  const { publications, loading, error, hasMore, loadMore } = usePublications(
    {
      status: 'PUBLISHED',
      limit: 10,
    },
    tenantId
  );

  const observerRef = useRef<IntersectionObserver | null>(null);
  const lastPublicationRef = useRef<HTMLDivElement | null>(null);

  // Infinite scroll avec IntersectionObserver
  useEffect(() => {
    if (loading) return;

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (lastPublicationRef.current) {
      observerRef.current.observe(lastPublicationRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loading, hasMore, loadMore]);

  if (error) {
    return (
      <div className="feed-error">
        <div className="error-icon">⚠️</div>
        <h3>Erreur de chargement</h3>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="feed-container">
      <button
        onClick={() => navigate('/shop')}
        className="btn-back"
        style={{
          marginBottom: '1.5rem',
          padding: '0.75rem 1.5rem',
          background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          fontWeight: 600,
          fontSize: '1rem',
          boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
          transition: 'all 0.3s',
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.4)';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(59, 130, 246, 0.3)';
        }}
      >
        ← Retour à la boutique
      </button>

      <div className="feed-header">
        <h1>📢 Actualités des Folies Maraîchères</h1>
        <p className="feed-subtitle">
          Arrivages, promotions, événements et coulisses de la ferme
        </p>
      </div>

      {newsletterState && newsletterMessage[newsletterState] && (
        <div
          className={`mb-5 flex items-start justify-between gap-3 rounded-xl px-4 py-3 text-sm ${
            newsletterMessage[newsletterState].tone === 'ok'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-amber-50 text-amber-800 border border-amber-200'
          }`}
        >
          <span>{newsletterMessage[newsletterState].text}</span>
          <button onClick={dismissNewsletterMessage} className="text-lg leading-none" aria-label="Fermer">×</button>
        </div>
      )}

      {/* S'abonner par email (sans compte) */}
      <div className="mb-6">
        <NewsletterSignup source="feed" variant="card" />
      </div>

      {/* Filtres rapides */}
      <div className="feed-filters">
        <button className="filter-chip active">
          Tout
        </button>
        <button className="filter-chip">
          🍅 Arrivages
        </button>
        <button className="filter-chip">
          🎉 Promotions
        </button>
        <button className="filter-chip">
          🌱 Anti-gaspi
        </button>
        <button className="filter-chip">
          🎪 Événements
        </button>
      </div>

      {/* Liste des publications */}
      <div className="publications-grid">
        {publications.length === 0 && !loading ? (
          <div className="empty-feed">
            <div className="empty-icon">📭</div>
            <h3>Aucune publication pour le moment</h3>
            <p>Revenez bientôt pour découvrir les nouveautés !</p>
          </div>
        ) : (
          publications.map((publication, index) => (
            <div
              key={publication.id}
              ref={index === publications.length - 1 ? lastPublicationRef : null}
            >
              <PublicationCard
                publication={publication}
                onView={() => {}} // Fonction vide - le tracking se fait uniquement dans la modale
                customerId={customerId}
                onOpenModal={setSelectedPublicationId}
              />
            </div>
          ))
        )}
      </div>

      {/* Loader */}
      {loading && (
        <div className="feed-loader">
          <div className="spinner"></div>
          <p>Chargement...</p>
        </div>
      )}

      {/* Fin du feed */}
      {!hasMore && publications.length > 0 && (
        <div className="feed-end">
          <p>✨ Vous êtes à jour !</p>
        </div>
      )}

      {/* Modale de détail */}
      {selectedPublicationId && (
        <PublicationModal
          publicationId={selectedPublicationId}
          tenantId={tenantId}
          customerId={customerId}
          onClose={() => setSelectedPublicationId(null)}
        />
      )}
    </div>
  );
};

export default Feed;
